/**
 * The Run view's front door for a PROJECT USER — see `routers/runapp.py`.
 *
 * `runAppBootstrap` is public and answers the one thing a Run URL cannot: the
 * application's owner, which every project-user endpoint is keyed by. Without
 * it a person holding a valid invitation has no way to reach their own sign-in
 * page. `runAppIdentity` is their counterpart to the Twasta account's
 * `run-identity` — the roles and object visibility the menu is filtered by.
 */

import api from './client'
import type { SecurityContextState } from './userMemory'

export interface RunAppInfo {
  owner_user_id: string
  project: string
  tenant_id: string
  title: string
  icon: string | null
  theme_color: string | null
  /** Whether this application accepts project-user sign-in. */
  project_users: boolean
  require_login: boolean
}

export interface RunAppIdentity {
  username: string
  email: string | null
  roles: string[]
  is_admin: boolean
  authors_app: boolean
  granted_objects: string[]
  denied_objects: string[]
  /** Deny by default: hide every node whose object is not in
   *  `granted_objects`, matching what the endpoints enforce. False for a
   *  func-admin, who is exempt from the object gate. */
  require_grant?: boolean
  owner_user_id: string
  kind: 'project-user'
  /** Context-based security: the entity `roles` are for (null when the app
   *  declares no security context). */
  security_context?: SecurityContextState | null
}

export function runAppBootstrap(tenant: string, project: string) {
  return api.get<RunAppInfo>(
    `/runapp/${encodeURIComponent(tenant)}/${encodeURIComponent(project)}/bootstrap`,
  )
}

export function runAppIdentity(project: string) {
  return api.get<RunAppIdentity>(`/runapp/${encodeURIComponent(project)}/identity`)
}

export interface RunAppAdopt {
  token: string
  expires_at: string
  owner_user_id: string
  user: { username: string; email: string; roles: string[] }
}

/**
 * Open this application as the signed-in Twasta account's OWN project-user
 * account — no second password. Succeeds only where the registry holds an
 * active account for the same (verified) email WITH a role on this
 * application; 403 otherwise, which is the honest answer that this person is
 * not a user of it. The session that comes back is an ordinary project-user
 * session: the identities stay separate, only the extra sign-in goes away.
 */
export function runAppAdopt(tenant: string, project: string) {
  return api.post<RunAppAdopt>(
    `/runapp/${encodeURIComponent(tenant)}/${encodeURIComponent(project)}/adopt`, {},
  )
}
